/**
 * The order a list is read in, as the API spells it.
 *
 * Sorting here is the server's job, not the table's: a page is fifty rows out
 * of a hundred thousand, and reordering those fifty answers a question nobody
 * asked. So a click on a header changes the `sort` the request carries, and the
 * server sends a different fifty back.
 *
 * The wire form is the one every admin list already takes — `field` ascending,
 * `-field` descending — and every service validates it against that endpoint's
 * own allowlist, refusing anything else with a 400. A column is therefore only
 * marked sortable when its `sortKey` is in that list; there is no way for the
 * console to ask for an order the server has not agreed to.
 */

export type SortDirection = 'asc' | 'desc'

export interface ListSort {
  field: string
  dir: SortDirection
}

/** `-created_at` → `{ field: 'created_at', dir: 'desc' }`. */
export function parseSort(value: string | null | undefined): ListSort | null {
  if (!value) return null
  return value.startsWith('-')
    ? { field: value.slice(1), dir: 'desc' }
    : { field: value, dir: 'asc' }
}

export function formatSort(sort: ListSort | null): string {
  if (!sort) return ''
  return sort.dir === 'desc' ? `-${sort.field}` : sort.field
}

/**
 * Which way a column opens on its first click.
 *
 * A name reads A→Z and a date reads newest-first, and getting that wrong makes
 * the first click feel broken — you ask for "sort by created" and land on the
 * oldest row in the tenant.
 */
export function defaultDirection(dir: SortDirection | undefined): SortDirection {
  return dir ?? 'asc'
}

/**
 * The three states a header cycles through: its default direction, the reverse,
 * then off.
 *
 * Off matters. The server's own default order is usually the useful one — most
 * recent first — and without a way back the only route to it is a reload.
 * Clicking a different column starts that column's cycle fresh.
 */
export function nextSort(
  current: string | null | undefined,
  field: string,
  preferred?: SortDirection,
): string {
  const first = defaultDirection(preferred)
  const active = parseSort(current)
  if (active?.field !== field) return formatSort({ field, dir: first })
  if (active.dir === first) {
    return formatSort({ field, dir: first === 'asc' ? 'desc' : 'asc' })
  }
  return ''
}
